import React, { useState, useEffect } from 'react'
import {
  Card, Table, Button, Space, Input, Select, Modal, Form, InputNumber,
  Tag, Tooltip, Popconfirm, Row, Col, Statistic, Alert, App
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined, DollarOutlined,
  SearchOutlined, ReloadOutlined
} from '@ant-design/icons'
import { modelConfigApi } from '@/services/api'
import dayjs from 'dayjs'

interface ModelConfig {
  id: number
  model_id: string
  model_name: string
  provider: string
  input_price: number
  output_price: number
  currency: string
  item_id?: string
  status: string
  effective_date: number
  created_at: number
  updated_at: number
  // 🔥 新增：model_configs表的字段
  model_type?: string
  tier?: string
  context_window?: number
  max_output_tokens?: number
  supports_vision?: boolean
  supports_audio?: boolean
  supports_code?: boolean
  supports_reasoning?: boolean
  supports_function_calling?: boolean
  supports_streaming?: boolean
  avg_response_time_ms?: number
  tokens_per_second?: number
  reliability_score?: number
  is_active?: boolean
  is_deprecated?: boolean
}

const ModelPricingPage: React.FC = () => {
  const { message } = App.useApp()
  const [models, setModels] = useState<ModelConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [batchEditModalVisible, setBatchEditModalVisible] = useState(false)
  const [editingModel, setEditingModel] = useState<ModelConfig | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [filters, setFilters] = useState({
    status: '',
    provider: '',
    search: ''
  })
  const [providers, setProviders] = useState<string[]>([])
  const [form] = Form.useForm()
  const [batchForm] = Form.useForm()

  // 获取模型列表
  const fetchModels = async () => {
    setLoading(true)
    try {
      // 🔥 修复：使用带有model_configs关联的API
      const response = await modelConfigApi.getModelConfigsWithDetails(filters)
      if (response.success) {
        console.log('🔍 模型配置数据:', response.data)
        setModels(response.data.models || [])
      } else {
        console.error('❌ 获取模型列表失败:', response.error)
        message.error('获取模型列表失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('获取模型列表失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 获取提供商列表
  const fetchProviders = async () => {
    try {
      const response = await modelConfigApi.getProviders()
      if (response.success) {
        setProviders(response.data || [])
      }
    } catch (error) {
      console.error('获取提供商列表失败:', error)
    }
  }

  // 同步proxy.js模型配置
  const syncFromProxy = async () => {
    setLoading(true)
    try {
      const response = await modelConfigApi.syncFromProxy()
      if (response.success) {
        const { total, added, updated } = response.data
        message.success(`同步成功，处理了 ${total} 个模型（新增 ${added} 个，更新 ${updated} 个）`)
        fetchModels()
      } else {
        message.error('同步失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('同步失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 🔥 新增：强制刷新模型列表
  const forceRefreshModels = async () => {
    setLoading(true)
    try {
      // 首先调用proxy.js的强制刷新API
      const refreshResponse = await fetch('http://localhost:8001/models/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || 'refly-zhangxin1212122025'}`
        }
      })

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json()
        message.success(`Proxy模型列表已刷新，获取到 ${refreshData.modelCount || 0} 个模型`)

        // 然后同步到后台管理系统
        await syncFromProxy()
      } else {
        message.error('强制刷新失败，请检查proxy.js服务状态')
      }
    } catch (error: any) {
      message.error('强制刷新失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 编辑模型
  const handleEdit = (model: ModelConfig) => {
    setEditingModel(model)
    form.setFieldsValue({
      model_id: model.model_id,
      model_name: model.model_name,
      provider: model.provider,
      input_price: model.input_price,
      output_price: model.output_price,
      currency: model.currency,
      item_id: model.item_id,
      status: model.status
    })
    setEditModalVisible(true)
  }

  // 保存编辑
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      
      if (editingModel) {
        // 更新
        const response = await modelConfigApi.updateModel(editingModel.id.toString(), values)
        if (response.success) {
          message.success('更新成功')
          setEditModalVisible(false)
          fetchModels()
        } else {
          message.error('更新失败: ' + response.error)
        }
      } else {
        // 创建
        const response = await modelConfigApi.createModel(values)
        if (response.success) {
          message.success('创建成功')
          setEditModalVisible(false)
          fetchModels()
        } else {
          message.error('创建失败: ' + response.error)
        }
      }
    } catch (error: any) {
      message.error('操作失败: ' + error.message)
    }
  }

  // 删除模型
  const handleDelete = async (id: number) => {
    try {
      const response = await modelConfigApi.deleteModel(id.toString())
      if (response.success) {
        message.success('删除成功')
        fetchModels()
      } else {
        message.error('删除失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('删除失败: ' + error.message)
    }
  }

  // 批量更新价格
  const handleBatchUpdatePrices = async () => {
    try {
      const values = await batchForm.validateFields()
      const selectedModels = models.filter(model => selectedRowKeys.includes(model.id))
      
      const updates = selectedModels.map(model => ({
        id: model.id,
        input_price: values.input_price ?? model.input_price,
        output_price: values.output_price ?? model.output_price
      }))

      const response = await modelConfigApi.batchUpdatePrices(updates)
      if (response.success) {
        message.success(`批量更新成功，更新了 ${response.data.updated} 个模型`)
        setBatchEditModalVisible(false)
        setSelectedRowKeys([])
        fetchModels()
      } else {
        message.error('批量更新失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('批量更新失败: ' + error.message)
    }
  }

  useEffect(() => {
    fetchModels()
    fetchProviders()
  }, [])

  useEffect(() => {
    fetchModels()
  }, [filters])

  const columns = [
    {
      title: '模型ID',
      dataIndex: 'model_id',
      key: 'model_id',
      width: 200,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {text.length > 30 ? `${text.substring(0, 30)}...` : text}
          </span>
        </Tooltip>
      )
    },
    {
      title: '模型名称',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 150,
      render: (text: string) => (
        <span style={{ fontWeight: 500 }}>{text}</span>
      )
    },
    {
      title: '提供商',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (text: string) => (
        <Tag color="blue">{text}</Tag>
      )
    },
    {
      title: '模型配置',
      key: 'model_config',
      width: 200,
      render: (_: any, record: ModelConfig) => (
        <div>
          {/* 🔥 新增：显示模型类型和等级 */}
          <div style={{ marginBottom: 4 }}>
            {record.model_type && (
              <Tag color="green">{record.model_type}</Tag>
            )}
            {record.tier && (
              <Tag color="purple">{record.tier}</Tag>
            )}
          </div>
          {/* 🔥 新增：显示模型能力 */}
          <div>
            {record.supports_vision && <Tag color="orange">视觉</Tag>}
            {record.supports_reasoning && <Tag color="red">推理</Tag>}
            {record.supports_code && <Tag color="geekblue">代码</Tag>}
            {record.supports_function_calling && <Tag color="cyan">函数</Tag>}
          </div>
        </div>
      )
    },
    {
      title: '输入价格',
      dataIndex: 'input_price',
      key: 'input_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => (
        <span style={{ fontFamily: 'monospace' }}>
          ${(Number(price) || 0).toFixed(6)}/1M tokens
        </span>
      )
    },
    {
      title: '输出价格',
      dataIndex: 'output_price',
      key: 'output_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => (
        <span style={{ fontFamily: 'monospace' }}>
          ${(Number(price) || 0).toFixed(6)}/1M tokens
        </span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (timestamp: number) => dayjs(timestamp).format('MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: ModelConfig) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个模型配置吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys)
    }
  }

  // 🔥 修复：统计数据计算，防止NaN错误
  const totalModels = models.length
  const activeModels = models.filter(m => m.status === 'active').length

  // 🔥 修复：安全的价格计算，确保数值类型
  const avgInputPrice = models.length > 0 ?
    models.reduce((sum, m) => {
      const price = Number(m.input_price) || 0
      return sum + (isFinite(price) ? price : 0)
    }, 0) / models.length : 0

  const avgOutputPrice = models.length > 0 ?
    models.reduce((sum, m) => {
      const price = Number(m.output_price) || 0
      return sum + (isFinite(price) ? price : 0)
    }, 0) / models.length : 0

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计概览 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总模型数"
              value={totalModels}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="启用模型"
              value={activeModels}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均输入价格"
              value={avgInputPrice}
              precision={6}
              prefix="$"
              suffix="/1M"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均输出价格"
              value={avgOutputPrice}
              precision={6}
              prefix="$"
              suffix="/1M"
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="模型价格管理"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={forceRefreshModels}
              loading={loading}
              type="default"
            >
              强制刷新模型
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={syncFromProxy}
              loading={loading}
            >
              同步Proxy配置
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingModel(null)
                form.resetFields()
                setEditModalVisible(true)
              }}
            >
              添加模型
            </Button>
          </Space>
        }
      >
        {/* 筛选器 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="搜索模型名称或ID"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="选择提供商"
              value={filters.provider}
              onChange={(value) => setFilters({ ...filters, provider: value })}
              allowClear
              style={{ width: '100%' }}
            >
              {providers.map(provider => (
                <Select.Option key={provider} value={provider}>
                  {provider}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="选择状态"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="active">启用</Select.Option>
              <Select.Option value="inactive">禁用</Select.Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchModels}
              loading={loading}
            >
              刷新
            </Button>
          </Col>
          {selectedRowKeys.length > 0 && (
            <Col span={6}>
              <Button
                type="primary"
                onClick={() => setBatchEditModalVisible(true)}
              >
                批量更新价格 ({selectedRowKeys.length})
              </Button>
            </Col>
          )}
        </Row>

        {/* 提示信息 */}
        <Alert
          message="价格管理说明"
          description="价格单位为美元/百万tokens (1M tokens)。修改价格后会影响后续的计费计算。建议定期从proxy.js同步最新的模型配置。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 表格 */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={models}
          rowKey="id"
          loading={loading}
          pagination={{
            total: models.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title={editingModel ? '编辑模型配置' : '添加模型配置'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            currency: 'USD',
            status: 'active'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="模型ID"
                name="model_id"
                rules={[{ required: true, message: '请输入模型ID' }]}
              >
                <Input placeholder="例如: openai/gpt-4" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="模型名称"
                name="model_name"
                rules={[{ required: true, message: '请输入模型名称' }]}
              >
                <Input placeholder="例如: GPT-4" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="提供商"
                name="provider"
                rules={[{ required: true, message: '请输入提供商' }]}
              >
                <Input placeholder="例如: OpenAI" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Select.Option value="active">启用</Select.Option>
                  <Select.Option value="inactive">禁用</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="输入价格 ($/1M tokens)"
                name="input_price"
                rules={[{ required: true, message: '请输入输入价格' }]}
              >
                <InputNumber
                  min={0}
                  step={0.000001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.000000"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="输出价格 ($/1M tokens)"
                name="output_price"
                rules={[{ required: true, message: '请输入输出价格' }]}
              >
                <InputNumber
                  min={0}
                  step={0.000001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.000000"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="货币"
                name="currency"
              >
                <Select>
                  <Select.Option value="USD">USD</Select.Option>
                  <Select.Option value="CNY">CNY</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Item ID"
                name="item_id"
              >
                <Input placeholder="可选，用于与proxy.js同步" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 批量编辑模态框 */}
      <Modal
        title={`批量更新价格 (${selectedRowKeys.length} 个模型)`}
        open={batchEditModalVisible}
        onOk={handleBatchUpdatePrices}
        onCancel={() => setBatchEditModalVisible(false)}
        width={500}
      >
        <Form form={batchForm} layout="vertical">
          <Alert
            message="批量更新说明"
            description="留空的字段将保持原值不变。只会更新选中模型的价格信息。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item
            label="输入价格 ($/1M tokens)"
            name="input_price"
          >
            <InputNumber
              min={0}
              step={0.000001}
              precision={6}
              style={{ width: '100%' }}
              placeholder="留空保持原值"
            />
          </Form.Item>
          
          <Form.Item
            label="输出价格 ($/1M tokens)"
            name="output_price"
          >
            <InputNumber
              min={0}
              step={0.000001}
              precision={6}
              style={{ width: '100%' }}
              placeholder="留空保持原值"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ModelPricingPage
